import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Code, Plus, Save, Trash2, Eye, EyeOff } from "lucide-react";

export default function AdminAds() {
  const [ads, setAds] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", position: "sidebar", page: "all", enabled: false });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    API.get("/admin/ads").then(r => setAds(r.data.codes || [])).catch(() => {});
  }, []);

  const create = async () => {
    setLoading("create");
    try {
      await API.post("/admin/ads", form);
      toast.success("Ad created");
      const { data } = await API.get("/admin/ads");
      setAds(data.codes || []);
      setShowCreate(false);
      setForm({ name: "", code: "", position: "sidebar", page: "all", enabled: false });
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const save = async (adId) => {
    setLoading(`save-${adId}`);
    try {
      await API.put(`/admin/ads/${adId}`, form);
      toast.success("Ad updated");
      const { data } = await API.get("/admin/ads");
      setAds(data.codes || []);
      setEditing(null);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const deleteAd = async (adId) => {
    try {
      await API.delete(`/admin/ads/${adId}`);
      toast.success("Ad deleted");
      setAds(prev => prev.filter(a => a.ad_id !== adId));
    } catch { toast.error("Failed"); }
  };

  const FormFields = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label className="text-[#94A3B8] text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
        <div>
          <Label className="text-[#94A3B8] text-xs">Position</Label>
          <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-sm" data-testid="ad-position-select">
            <option value="head">&lt;head&gt; (verification/analytics)</option>
            <option value="sidebar">Sidebar</option>
            <option value="top">Top Banner</option>
            <option value="bottom">Bottom</option>
            <option value="inline">Inline</option>
            <option value="footer">Footer (end of body)</option>
            <option value="all">All Positions</option>
          </select>
        </div>
        <div>
          <Label className="text-[#94A3B8] text-xs">Page</Label>
          <select value={form.page} onChange={(e) => setForm({ ...form, page: e.target.value })} className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 text-sm" data-testid="ad-page-select">
            <option value="all">All Pages</option>
            <option value="landing">Landing / Home</option>
            <option value="dashboard">Dashboard</option>
            <option value="mining">Extraction</option>
            <option value="inventory">Inventory</option>
            <option value="wallet">Wallet</option>
            <option value="tasks">Tasks</option>
            <option value="leaderboard">Leaderboard</option>
          </select>
        </div>
      </div>
      <div>
        <Label className="text-[#94A3B8] text-xs">HTML/JavaScript Ad Code</Label>
        <Textarea value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} rows={6} placeholder="Paste your ad HTML or script code here..." className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] text-sm mt-1 font-mono-game" />
        <p className="text-[10px] text-[#64748B] mt-1">Supports HTML, JavaScript, and iframe ad codes. Scripts will execute when the ad is enabled.</p>
      </div>
    </div>
  );

  return (
    <AdminLayout title="Ad Management">
      <div className="space-y-4" data-testid="admin-ads">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#94A3B8]">Add HTML/JS ad codes for monetization. All ads are disabled by default. Use position <span className="text-[#00F0FF]">&lt;head&gt;</span> for ad-network verification tags (AdSense, Bitmedia, analytics) and page <span className="text-[#00F0FF]">Landing</span> to target the public homepage.</p>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="create-ad-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg">
            <Plus className="w-3 h-3" /> New Ad
          </button>
        </div>

        {showCreate && (
          <div className="bg-[#131722] rounded-xl border border-[#00F0FF]/30 p-4 space-y-3" data-testid="create-ad-form">
            <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2"><Code className="w-4 h-4 text-[#00F0FF]" /> New Ad Code</h3>
            <FormFields />
            <button onClick={create} disabled={loading === "create"} className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50">Create (Disabled by Default)</button>
          </div>
        )}

        <div className="space-y-3">
          {ads.map(ad => (
            <div key={ad.ad_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4" data-testid={`ad-${ad.ad_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Code className="w-4 h-4 text-[#B026FF]" />
                  <div>
                    <p className="text-sm font-bold text-[#F8FAFC]">{ad.name || "Untitled"}</p>
                    <p className="text-xs text-[#64748B]">Position: {ad.position} | Page: {ad.page} | {ad.enabled ? <span className="text-[#10B981]">Active</span> : <span className="text-[#64748B]">Disabled</span>}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {editing === ad.ad_id ? (
                    <button onClick={() => save(ad.ad_id)} disabled={loading === `save-${ad.ad_id}`} className="p-1.5 text-[#10B981] hover:bg-[#10B981]/10 rounded"><Save className="w-4 h-4" /></button>
                  ) : (
                    <button onClick={() => { setEditing(ad.ad_id); setForm({ name: ad.name, code: ad.code, position: ad.position, page: ad.page, enabled: !ad.enabled }); }}
                      className={`p-1.5 rounded ${ad.enabled ? "text-[#10B981] hover:bg-[#10B981]/10" : "text-[#64748B] hover:bg-[#64748B]/10"}`}>
                      {ad.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  )}
                  <button onClick={() => deleteAd(ad.ad_id)} className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {editing === ad.ad_id && (
                <div className="mt-3 pt-3 border-t border-[#2A3241]/30"><FormFields /></div>
              )}
              {!editing && ad.code && <pre className="text-[10px] text-[#64748B] font-mono-game bg-[#0B0E14] rounded p-2 mt-2 max-h-20 overflow-auto">{ad.code.slice(0, 200)}{ad.code.length > 200 ? "..." : ""}</pre>}
            </div>
          ))}
          {ads.length === 0 && <p className="text-[#94A3B8] text-center py-6">No ad codes. Create one above and paste your ad provider&apos;s HTML/JS code.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
